import { useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import { WORKSPACE_PORTAL_URL } from "../services/config";
import { clearStoredSession } from "../services/session";
import { getIfasPortalLoginUrl } from "../utils/claimsPortal";

/**
 * Shown to an IFAS account that reaches the claims portal.
 *
 * IFAS moved into its own app (#2089). Where the IFAS portal is configured,
 * this forwards straight there: the Cognito session is shared, so its Login
 * button signs the user in without asking for credentials again. Where it is
 * not (production, until IFAS launches there), it explains and points at the
 * workspace instead of rendering a Claims dashboard the account cannot use.
 *
 * It clears this app's saved session first, in both cases. Without that, a
 * stale IFAS session left in this browser kept the claims login page
 * unreachable: /admin/login sends a signed-in visitor to /dashboard, and the
 * dashboard sent them straight back here, with no sign-out in between. Claims
 * testers coming through the workspace were caught in a loop between the two
 * portals (2026-10-05). Cleared, the next visit to this app shows its login.
 * The Cognito hosted session is left alone, so the IFAS portal can still sign
 * the user in without asking for credentials again.
 */
export default function IfasMovedNotice() {
  const target = getIfasPortalLoginUrl();

  useEffect(() => {
    // Storage only, not clearClientSession(): this page is leaving, and signing
    // the live page out would fire the route guards' in-page redirect while
    // the cross-site navigation below is still uncommitted.
    clearStoredSession();
    if (target) {
      window.location.replace(target);
    }
  }, [target]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        px: 3,
        textAlign: "center",
      }}
    >
      <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
        Funeral Administration has moved
      </Typography>
      <Typography sx={{ fontSize: 14, color: "text.secondary", maxWidth: 460 }}>
        {target
          ? "It now has its own portal. Taking you there…"
          : "It now has its own portal. Open it from the workspace."}
      </Typography>
      {target ? (
        <Button variant="contained" href={target}>
          Open Funeral Administration
        </Button>
      ) : (
        WORKSPACE_PORTAL_URL && (
          <Button variant="contained" href={WORKSPACE_PORTAL_URL}>
            Go to the workspace
          </Button>
        )
      )}
    </Box>
  );
}
